/**
 * Hash-router shell.
 *
 * Three routes for Phase 1:
 *   #/         → SignIn (or Home if a session JWT is in localStorage)
 *   #/verify   → handles ?token=… landing from the magic-link email
 *   #/         → Home (Phase 1 stub — Phase 2 fills in reservations/receipts)
 *
 * Hash routing keeps Cloudflare Pages SPA setup trivial — no _redirects /
 * _routes.json needed. The email link points at /verify?token=… and the
 * Verify screen reads it from window.location.search.
 */
import { useEffect, useState } from 'react'
import { SignIn } from './auth/SignIn'
import { Verify } from './auth/Verify'
import { Home } from './screens/Home'
import { FindParking } from './screens/FindParking'
import { AveryAsk, AverySupportButton } from './components/AveryAsk'
import { SignedInToast } from './components/SignedInToast'
import { SupportButton } from '@parkify/ui'
import { getToken, clearSession } from './api'

type Route = 'signin' | 'verify' | 'home' | 'find'

function currentRoute(): Route {
  // /verify is the customer-facing URL embedded in the magic-link email
  // (path-based — clean URL, not /#/verify). All other in-app navigation
  // uses hash routing so Cloudflare Pages doesn't need a _redirects file.
  // Verify.tsx clears the pathname back to '/' after successful sign-in so
  // we don't loop back into the verify state on re-render.
  const pathname = window.location.pathname
  if (pathname.startsWith('/verify')) return 'verify'
  const hash = window.location.hash.replace(/^#/, '')
  if (hash.startsWith('/verify')) return 'verify'
  // Public find-parking browse (slice 5) — reachable signed in or out.
  if (hash.startsWith('/find')) return 'find'
  // The old sign-in card stays directly reachable (support links, muscle
  // memory); the primary logged-out entry is the in-page slide-over on /find.
  if (hash.startsWith('/signin')) return 'signin'
  if (getToken()) return 'home'
  // Signed-out landing: account.parkify.com doubles as the public "find
  // parking" page — a browsing experience, not a sign-in wall.
  return 'find'
}

export function App() {
  const [route, setRoute] = useState<Route>(currentRoute())
  // Bumped on sign-in/out that doesn't change `route` (the in-page slide-over
  // on /find, or signing out ON /find) — forces a re-render so FindParking's
  // `signedIn={!!getToken()}` re-reads the token instead of going stale. Value
  // itself is never read, only the setter (state changes are what re-render).
  const [, setAuthVersion] = useState(0)
  // All three sign-in surfaces (phone OTP, email magic-link, the in-page
  // slide-over on /find) otherwise land the visitor on Home/find with zero
  // feedback that anything happened. This flips true for a few seconds right
  // after a fresh sign-in so SignedInToast can confirm it, then clears itself.
  const [justSignedIn, setJustSignedIn] = useState(false)

  useEffect(() => {
    const onHashChange = () => setRoute(currentRoute())
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [])

  useEffect(() => {
    if (!justSignedIn) return
    const t = setTimeout(() => setJustSignedIn(false), 3000)
    return () => clearTimeout(t)
  }, [justSignedIn])

  const goHome = () => {
    window.location.hash = '/'
    setRoute(currentRoute())
  }

  const goHomeSignedIn = () => {
    setJustSignedIn(true)
    goHome()
  }

  const signOut = () => {
    clearSession()
    window.location.hash = '/'
    // Signed-out root is the public find-parking landing (slice 5); it
    // carries its own in-page Log in affordance. Signing out ON /find lands
    // back on /find, so the route value is unchanged and setRoute alone would
    // be a no-op re-render — the page's "Signed in" row would go stale (looks
    // like sign-out did nothing). Bump authVersion to force it to recompute.
    setAuthVersion((v) => v + 1)
    setRoute(currentRoute())
  }

  return (
    <div className="app-shell">
      {/* `signedIn` is read fresh on every render (same pattern as FindParking's
          prop below) — the authVersion bump on an in-page sign-in/out re-renders
          App, so the header swaps its Support control without a route change. */}
      <Header showAsk={route === 'home'} signedIn={!!getToken()} />
      <main style={{ flex: 1, padding: '32px 16px', display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: 540 }}>
          {justSignedIn && <SignedInToast />}
          {route === 'signin' && <SignIn onSignedIn={goHomeSignedIn} />}
          {route === 'verify' && <Verify onSuccess={goHomeSignedIn} />}
          {route === 'home' && <Home onSignOut={signOut} />}
          {route === 'find' && (
            // onAccount routes to the account view via setRoute directly, NOT a
            // bare `#/` link: after an in-page slide-over sign-in the hash is
            // often ALREADY `#/` (e.g. left there by a prior sign-out), so an
            // <a href="#/"> fires no hashchange and the router never updates —
            // "My account" would look dead. goHome sets the route unconditionally.
            <FindParking
              signedIn={!!getToken()}
              onAuthed={() => { setJustSignedIn(true); setAuthVersion(v => v + 1) }}
              onAccount={goHome}
            />
          )}
        </div>
      </main>
      <Footer />
    </div>
  )
}

function Header({ showAsk, signedIn }: { showAsk: boolean; signedIn: boolean }) {
  // Layout + responsive behaviour live in index.css (.portal-header*): on narrow
  // phones the decorative "My Account" wordmark drops out and the row wraps as a
  // last resort, so Support never bleeds off the right edge.
  const goFind = () => { window.location.hash = '/find' }
  return (
    <header className="portal-header">
      <div className="portal-header__bar">
        <div className="portal-header__brand">
          {/* Band logo doubles as "home" — jumps to the browsable find-parking
              surface from anywhere in the portal. Plain button, no chrome, so it
              still just reads as the logo. */}
          <button type="button" className="portal-header__logo-btn" onClick={goFind} aria-label="Parkify — home">
            <img
              src="/parkify-logo.svg"
              alt="Parkify"
              height={22}
              style={{ display: 'block' }}
            />
          </button>
          <span aria-hidden className="portal-header__rule" />
          <span className="portal-header__acct">My Account</span>
        </div>
        {/* Support escape hatch, always reachable — but signed in it means
            something better than a phone number. A verified customer gets the
            person icon straight into Avery chat: she already knows who they
            are, so receipts, charges, extends and refund requests get handled
            in-thread instead of routing them to SMS/voice to re-identify
            themselves. Signed out, /api/customer/chat has no session to talk
            about, so the brand-standard Text/Call popover stays.
            (Sign out lives in the Account tab's Settings list — Slack,
            2026-07-17 — not in this header.) */}
        <div className="portal-header__actions">
          {signedIn ? <AverySupportButton /> : <SupportButton tone="light" />}
        </div>
      </div>
      {/* Ask Avery lives in the header as a prompt box on the signed-in Home;
          the find-parking browse keeps the lean header. */}
      {showAsk && (
        <div className="portal-header__ask">
          <AveryAsk />
        </div>
      )}
    </header>
  )
}

function Footer() {
  return (
    <footer style={{
      padding: '24px 32px',
      textAlign: 'center',
      fontSize: 12,
      color: 'var(--color-gray-600)',
    }}>
      © {new Date().getFullYear()} Parkify
    </footer>
  )
}
